/**
 * Reload the page at most once per short window.
 *
 * Stale-deploy recovery reloads — a failed chunk `import()` (`vite:preloadError`)
 * or a version-check mismatch — can otherwise loop forever when the reload keeps
 * re-serving a stale `index.html` (notably iOS Safari / an installed PWA web
 * view), hammering the user with refreshes and Sentry with events. After one
 * reload, if we're asked again within the window we stop and let the error
 * surface (error boundary) instead of refreshing endlessly.
 *
 * @returns true if a reload was triggered, false if suppressed to break a loop.
 */
const RELOAD_GUARD_KEY = "app:last-recovery-reload";
const RELOAD_GUARD_WINDOW_MS = 10_000;

export function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      // We reloaded moments ago and it didn't resolve the failure — reloading
      // again would loop. Stop and let the error propagate.
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode, etc.) — fall through and reload.
  }
  window.location.reload();
  return true;
}
